/* Sample only the visible crop behind News-card text; never alter the article. */
(() => {
  const imageCache = new Map();
  const linear = (value) => {
    const channel = value / 255;
    return channel <= .04045 ? channel / 12.92 : ((channel + .055) / 1.055) ** 2.4;
  };
  const loadImage = (url) => {
    if (!imageCache.has(url)) {
      imageCache.set(url, new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Image sampling unavailable'));
        image.src = url;
      }));
    }
    return imageCache.get(url);
  };
  const initialise = (root) => {
    root.querySelectorAll('.blog__item--3').forEach((card) => {
      if (card.dataset.contrastReady) return;
      card.dataset.contrastReady = 'true';
      const image = card.querySelector('.article__photo-wrapper img');
      const meta = card.querySelector('.content-block__meta');
      if (!image || !meta) return; // Solid dark fallback also covers imageless cards.
      let frame = 0;
      let version = 0;
      const update = async () => {
        const current = ++version;
        const bounds = image.getBoundingClientRect();
        const children = Array.from(meta.children)
          .map((child) => child.getBoundingClientRect())
          .filter((rect) => rect.width && rect.height);
        if (!bounds.width || !bounds.height || !children.length) return;
        const top = Math.min(...children.map((rect) => rect.top));
        const bottom = Math.max(...children.map((rect) => rect.bottom));
        const left = Math.min(...children.map((rect) => rect.left));
        const right = Math.max(...children.map((rect) => rect.right));
        const metaBounds = meta.getBoundingClientRect();
        card.style.setProperty('--idyllic-news-scrim-height',
          `${Math.ceil(metaBounds.bottom - top + 48)}px`);
        card.style.setProperty('--idyllic-news-fade-height',
          `${Math.max(0, Math.min(36, top - metaBounds.top - 12))}px`);
        // The initial 1px lazy-load placeholder isn't a useful sample.
        if (image.naturalWidth < 8) return;
        const url = image.currentSrc || image.src;
        try {
          const source = await loadImage(url);
          if (current !== version) return;
          // Match object-fit: cover and the theme's centred image positioning.
          const scale = Math.max(bounds.width / source.width, bounds.height / source.height);
          const cropWidth = bounds.width / scale;
          const cropHeight = bounds.height / scale;
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = 64;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(source,
            (source.width - cropWidth) / 2, (source.height - cropHeight) / 2,
            cropWidth, cropHeight, 0, 0, 64, 64);
          const x = Math.max(0, Math.min(63, Math.floor((left - bounds.left) / bounds.width * 64)));
          const y = Math.max(0, Math.min(63, Math.floor((top - bounds.top) / bounds.height * 64)));
          const width = Math.max(1, Math.min(64 - x, Math.ceil((right - left) / bounds.width * 64)));
          const height = Math.max(1, Math.min(64 - y, Math.ceil((bottom - top) / bounds.height * 64)));
          const pixels = context.getImageData(x, y, width, height).data;
          let total = 0;
          for (let i = 0; i < pixels.length; i += 4) {
            total += .2126 * linear(pixels[i]) + .7152 * linear(pixels[i + 1]) + .0722 * linear(pixels[i + 2]);
          }
          // The crossover where black and white have equal WCAG contrast.
          card.classList.toggle('is-light-image', total / (pixels.length / 4) > .179);
          card.classList.add('is-contrast-sampled');
        } catch {
          // CORS, unavailable images or canvas restrictions: retain readable white-on-dark.
          if (current === version) {
            card.classList.remove('is-light-image', 'is-contrast-sampled');
          }
        }
      };
      const schedule = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(update);
      };
      image.addEventListener('load', schedule);
      image.addEventListener('lazyloaded', schedule);
      new MutationObserver(schedule).observe(image, { attributes: true, attributeFilter: ['src', 'srcset'] });
      if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(card);
      else window.addEventListener('resize', schedule);
      schedule();
    });
  };
  const start = () => document.querySelectorAll('.idyllic-news-contrast').forEach(initialise);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
